import { AuthUser } from '../../../services/authService';
import { apiPatch } from '../../../utils/apiClient';

interface PersistUserBalanceParams {
  balance: number;
  gamesPlayedIncrement?: number;
}

interface PersistUserBalanceResult {
  balance: number;
  gameCount: number;
}

interface PersistUserBalanceOptions {
  user: AuthUser | null;
  accessToken: string | null;
  apiBaseUrl: string;
  refreshAccessToken: () => Promise<string | null>;
  updateUser: (user: AuthUser) => void;
}

/**
 * 사용자 잔고를 서버에 저장하고 로컬 상태 업데이트
 */
export const persistUserBalance = async (
  params: PersistUserBalanceParams,
  options: PersistUserBalanceOptions
): Promise<PersistUserBalanceResult> => {
  const { balance, gamesPlayedIncrement = 0 } = params;
  const { user, accessToken, apiBaseUrl, refreshAccessToken, updateUser } =
    options;

  if (!user) {
    return { balance, gameCount: gamesPlayedIncrement };
  }

  const applyLocalUpdate = (
    nextBalance: number,
    nextGameCount?: number
  ): PersistUserBalanceResult => {
    const resolvedGameCount =
      nextGameCount !== undefined
        ? nextGameCount
        : (user.gameCount ?? 0) + gamesPlayedIncrement;

    const updatedUser: AuthUser = {
      ...user,
      balance: nextBalance,
      gameCount: resolvedGameCount,
    };
    updateUser(updatedUser);

    return {
      balance: nextBalance,
      gameCount: resolvedGameCount,
    };
  };

  if (!accessToken) {
    return applyLocalUpdate(balance);
  }

  try {
    // apiClient를 사용하여 백엔드의 공통 응답 구조를 자동으로 처리
    // apiPatch는 ResponseInterceptor가 래핑한 응답에서 자동으로 data를 추출합니다.
    interface BalanceResponse {
      balance: number;
      gameCount: number;
    }

    const responseData = await apiPatch<any>(`${apiBaseUrl}/api/user/balance`, {
      balance,
      gamesPlayedIncrement,
    });

    console.log('💰 잔고 업데이트 응답:', {
      responseData,
      responseDataType: typeof responseData,
      responseDataKeys: responseData ? Object.keys(responseData) : [],
      hasData: responseData?.data !== undefined,
      hasBalance:
        responseData?.balance !== undefined ||
        responseData?.data?.balance !== undefined,
    });

    // ResponseInterceptor가 { data: { data: { balance, gameCount } } } 형태로 감싸거나
    // { data: { balance, gameCount } } 형태일 수 있으므로 둘 다 처리
    const balanceData =
      responseData?.data?.data || responseData?.data || responseData;
    const updatedBalance =
      typeof balanceData.balance === 'number' ? balanceData.balance : balance;
    const updatedGameCount =
      typeof balanceData.gameCount === 'number'
        ? balanceData.gameCount
        : undefined;

    console.log('💰 최종 업데이트 값:', {
      updatedBalance,
      updatedGameCount,
    });

    return applyLocalUpdate(updatedBalance, updatedGameCount);
  } catch (error) {
    console.error('사용자 잔고 업데이트 실패:', error);
    return applyLocalUpdate(balance);
  }
};
